import * as React from "react";
import { graphql, compose } from "react-apollo";
import gql from "graphql-tag";
import Button from "@material-ui/core/Button";
import Dialog from "material-ui/Dialog";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import { UserMessage } from "./userMessageTypes";
import ReactFileReader from "react-file-reader";
import handleMutationError from "app/utils/handleMutationError";

type Props = {
  userMessage: UserMessage | null;
  approveUserMessage: Function;
  onClose: any;
};

type State = {
  approveType: string | null;
  approveUserIds: Array<string>;
  approving: boolean;
};

const TRUNCATE_LIMIT: number = 10;

const getTruncatedArray = (ar: Array<string>) => {
  let retVal = "";
  for (let i = 0; i < Math.min(ar.length, TRUNCATE_LIMIT); i++) {
    if (i === ar.length - 1) {
      retVal += ar[i];
    } else {
      retVal += ar[i] + ", ";
    }
  }
  if (ar.length > TRUNCATE_LIMIT) {
    retVal += "...";
  }
  return retVal;
};

class ApproveModal extends React.Component<Props, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      approveType: null,
      approveUserIds: [],
      approving: false,
    };
  }

  handleChange = (event) => {
    this.setState({ approveType: event.target.value });
  };

  onApprove = (): void => {
    const params: any = {
      id: this.props.userMessage.id,
      user_targeting_type: this.state.approveType,
    };
    if (this.state.approveType === "group") {
      params.user_ids = this.state.approveUserIds;
    }
    this.setState({ approving: true });
    this.props
      .approveUserMessage({
        variables: params,
      })
      .then(() => {
        this.setState({
          approveType: null,
          approveUserIds: [],
          approving: false,
        });
        this.props.onClose({ refetchData: true });
      })
      .catch((error) => {
        this.setState({
          approving: false,
        });
        handleMutationError(error);
      });
  };

  onCsvUpload = (files) => {
    let reader = new FileReader();
    reader.onload = (e) => {
      const usersCsv = (reader.result as any).split("\n");
      this.setState({
        approveUserIds: usersCsv.filter(Number),
      });
    };
    reader.readAsText(files[0]);
  };

  render() {
    return (
      <Dialog
        title="Approve User Message as type:"
        modal={false}
        open={this.props.userMessage != null}
        actions={[
          <Button color="secondary" onClick={() => this.props.onClose()}>
            Cancel
          </Button>,
          <Button
            variant="contained"
            color="primary"
            onClick={this.onApprove}
            disabled={this.state.approving}
          >
            {this.state.approving ? "Approving..." : "Approve"}
          </Button>,
        ]}
        onRequestClose={() => this.props.onClose()}
      >
        <FormControl>
          <RadioGroup
            aria-label="approve"
            name="approve"
            value={this.state.approveType}
            onChange={this.handleChange}
            style={{
              display: "flex",
              justifyContent: "flex-start",
              flexDirection: "row",
            }}
          >
            <FormControlLabel
              value="global"
              control={<Radio color="primary" />}
              label="Global"
              labelPlacement="start"
            />
            <FormControlLabel
              value="new_signup"
              control={<Radio color="primary" />}
              label="New Signups"
              labelPlacement="start"
            />
            <FormControlLabel
              value="group"
              control={<Radio color="primary" />}
              label="Group"
              labelPlacement="start"
            />
            <FormControlLabel
              value="ab_test_a"
              control={<Radio color="primary" />}
              label="A/B test group A"
              labelPlacement="start"
            />
            <FormControlLabel
              value="ab_test_b"
              control={<Radio color="primary" />}
              label="A/B test group B"
              labelPlacement="start"
            />
            <FormControlLabel
              value="cashback"
              control={<Radio color="primary" />}
              label="Cashback"
              labelPlacement="start"
            />
          </RadioGroup>
          {this.state.approveType === "group" ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                marginTop: "8px",
              }}
            >
              <FormHelperText>
                Please enter User ids in comma separated values, or upload CSV
              </FormHelperText>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  marginTop: "8px",
                }}
              >
                <label>{`${this.state.approveUserIds.length} users added.`}</label>
                <ReactFileReader
                  handleFiles={this.onCsvUpload}
                  fileTypes={".csv"}
                >
                  <Button
                    style={{ marginLeft: "10px" }}
                    variant="contained"
                    color="secondary"
                  >
                    Upload CSV
                  </Button>
                </ReactFileReader>
                <Button
                  onClick={() => this.setState({ approveUserIds: [] })}
                  style={{ marginLeft: "10px" }}
                  color="default"
                >
                  Clear
                </Button>
              </div>
            </div>
          ) : null}
          {this.state.approveUserIds.length > 0 ? (
            <label>{`Users: ${getTruncatedArray(
              this.state.approveUserIds
            )}`}</label>
          ) : null}
          <FormHelperText>
            <b>
              NB: Note that once a message is approved, users will begin to
              receive it
            </b>
          </FormHelperText>
        </FormControl>
      </Dialog>
    );
  }
}

const approveUserMessage = graphql<Props, any, any>(
  gql`
    mutation approveUserMessage(
      $id: ID!
      $user_targeting_type: String!
      $user_ids: [String]
    ) {
      approve_user_message(
        id: $id
        user_targeting_type: $user_targeting_type
        user_ids: $user_ids
      ) {
        id
      }
    }
  `,
  { name: "approveUserMessage" }
);

export default compose(approveUserMessage)(ApproveModal);
